import React, { useEffect, useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  useWindowDimensions,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import {
  getGroupArtifacts,
  Artifact,
  updateGroupArtifactPrices,
} from '@/services/artifacts/artifactService';
import { formatPrice, formatChangePercent } from '@/services/stock/stockService';
import { MiniSparkline } from '@/components/charts/MiniSparkline';
import { IntelligentOutlookTab } from '@/screens/stock/components/IntelligentOutlookTab';
import { ChartTab } from '@/screens/stock/components/ChartTab';
import { StockChatScreen } from '@/screens/stock/StockChatScreen';
import { getFullStockConversationHistory } from '@/services/history/searchHistoryService';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';
import { useMarketData } from '@/hooks/useMarketData';
import { useStockSparklines } from '@/hooks/useStockSparklines';

type TabType = 'outlook' | 'chart' | 'askAI';

interface ArtifactsSidebarProps {
  groupId: string;
  selectedTicker: string | null;
  onSelectTicker: (ticker: string | null) => void;
  onClose: () => void;
  navigation?: any;
}

export const ArtifactsSidebar: React.FC<ArtifactsSidebarProps> = ({
  groupId,
  selectedTicker: externalSelectedTicker,
  onSelectTicker,
  onClose,
  navigation,
}) => {
  console.log('🎨🎨🎨 [NEW ARTIFACTS SIDEBAR] Component mounted! GroupId:', groupId);
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;
  console.log(
    '🎨🎨🎨 [NEW ARTIFACTS SIDEBAR] Screen width:',
    width,
    'isLargeScreen:',
    isLargeScreen
  );

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [internalSelectedTicker, setInternalSelectedTicker] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('outlook');
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [stockData, setStockData] = useState<any>(null);
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);

  // Toggle states for message count filtering
  type StockFilter = 'all' | 'once' | 'multiple';
  const [stockFilter, setStockFilter] = useState<StockFilter>('all'); // Default: show all stocks
  const [includeAISentiment, setIncludeAISentiment] = useState(true); // Default: include AI sentiment

  // Get tickers for data fetching - memoize to prevent infinite re-renders
  const artifactTickers = useMemo(() => artifacts.map(a => a.ticker), [artifacts.length]);

  // Filter artifacts based on message count
  const filteredArtifacts = useMemo(() => {
    if (stockFilter === 'all') {
      return artifacts; // Show all stocks
    } else if (stockFilter === 'once') {
      return artifacts.filter(a => a.message_count === 1); // Mentioned once
    } else {
      return artifacts.filter(a => a.message_count > 1); // Mentioned multiple times
    }
  }, [artifacts, stockFilter]);

  // Fetch market data (price/change) and sparkline charts in parallel
  const {
    marketData,
    refreshData: refreshMarketData,
    isLoading: marketDataLoading,
  } = useMarketData(artifactTickers);
  const { chartDataMap, isLoading: chartsLoading } = useStockSparklines(artifactTickers);

  // Sync internal selection with external (for chat filtering)
  const selectedTicker = internalSelectedTicker;

  useEffect(() => {
    loadArtifacts();
  }, [groupId]);

  // Auto-select top stock on mount
  useEffect(() => {
    if (artifacts.length > 0 && !internalSelectedTicker) {
      const topStock = artifacts[0];
      handleSelectStock(topStock.ticker, topStock.name);
    }
  }, [artifacts]);

  const loadArtifacts = async () => {
    console.log('🎨 [NEW ARTIFACTS SIDEBAR] Loading artifacts for group:', groupId);
    const { data, error } = await getGroupArtifacts(groupId);

    if (error) {
      console.error('Error loading artifacts:', error);
    } else {
      console.log('🎨 [NEW ARTIFACTS SIDEBAR] Loaded', data?.length || 0, 'stocks');
      setArtifacts(data || []);
    }

    setLoading(false);
  };

  const handleSelectStock = async (ticker: string, companyName: string) => {
    console.log(`📊 [ArtifactsSidebar] Selected stock: ${ticker}`);

    setInternalSelectedTicker(ticker);
    setSelectedCompanyName(companyName);
    setActiveTab('outlook'); // Reset to outlook tab
    onSelectTicker(ticker); // Filter chat messages

    // Load stock data for the selected stock
    try {
      const data = await polygonMarketDataService.getStockData(ticker);
      setStockData(data);
    } catch (error) {
      console.error('Error loading stock data:', error);
    }

    // Load conversation history for the "Ask AI" tab
    try {
      console.log(`💬 [ArtifactsSidebar] Loading conversation history for ${ticker}`);
      const historyResult = await getFullStockConversationHistory(ticker);
      if (historyResult.success && historyResult.messages) {
        console.log(
          `💬 [ArtifactsSidebar] Loaded ${historyResult.messages.length} previous messages`
        );
        setConversationHistory(historyResult.messages);
      } else {
        console.log(`💬 [ArtifactsSidebar] No conversation history found for ${ticker}`);
        setConversationHistory([]);
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
      setConversationHistory([]);
    }

    // On mobile, show modal
    if (!isLargeScreen) {
      setShowMobileModal(true);
    }
  };

  const handleRefreshPrices = async () => {
    setRefreshing(true);

    // Refresh both artifacts metadata and live market data
    await Promise.all([updateGroupArtifactPrices(groupId), refreshMarketData()]);

    // Reload artifacts to show updated metadata
    await loadArtifacts();

    Alert.alert('Price Update Complete', 'All stock prices refreshed successfully', [
      { text: 'OK' },
    ]);

    setRefreshing(false);
  };

  const handleCloseMobileModal = () => {
    setShowMobileModal(false);
  };

  const renderStockItem = ({ item }: { item: Artifact }) => {
    const isSelected = selectedTicker === item.ticker;

    // Get live market data for this ticker
    const liveData = marketData.find(m => m.symbol === item.ticker);
    const isPositive = (liveData?.changePercent || 0) >= 0;
    const changeColor = isPositive ? Colors.market.profit : Colors.market.loss;

    return (
      <Pressable
        style={[styles.listItem, isSelected && styles.selectedItem]}
        onPress={() => handleSelectStock(item.ticker, item.name)}
      >
        {/* Left: Ticker & Name */}
        <View style={styles.leftSection}>
          <Text style={styles.ticker}>{item.ticker}</Text>
          <Text style={styles.companyName} numberOfLines={1}>
            {item.name}
          </Text>
        </View>

        {/* Middle: Sparkline Chart */}
        <View style={styles.chartSection}>
          {chartDataMap[item.ticker] ? (
            <MiniSparkline data={chartDataMap[item.ticker]} width={60} height={30} />
          ) : (
            <ActivityIndicator size="small" color={Colors.text.tertiary} />
          )}
        </View>

        {/* Right: Price & Change */}
        <View style={styles.rightSection}>
          {liveData ? (
            <>
              <Text style={styles.price}>${liveData.price.toFixed(2)}</Text>
              <Text style={[styles.change, { color: changeColor }]}>
                {isPositive ? '+' : ''}
                {liveData.changePercent.toFixed(2)}%
              </Text>
            </>
          ) : (
            <ActivityIndicator size="small" color={Colors.text.tertiary} />
          )}
        </View>

        {/* Far Right: Message Count Badge */}
        <View style={styles.messageBadge}>
          <Text style={styles.messageBadgeText}>{item.message_count}</Text>
        </View>
      </Pressable>
    );
  };

  const renderTabs = () => (
    <View style={styles.tabBar}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'outlook' && styles.activeTab]}
        onPress={() => setActiveTab('outlook')}
      >
        <Ionicons
          name="analytics"
          size={16}
          color={activeTab === 'outlook' ? Colors.accent.primary : Colors.text.secondary}
        />
        <Text style={[styles.tabText, activeTab === 'outlook' && styles.activeTabText]}>
          Outlook
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'chart' && styles.activeTab]}
        onPress={() => setActiveTab('chart')}
      >
        <Ionicons
          name="bar-chart"
          size={16}
          color={activeTab === 'chart' ? Colors.accent.primary : Colors.text.secondary}
        />
        <Text style={[styles.tabText, activeTab === 'chart' && styles.activeTabText]}>Chart</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'askAI' && styles.activeTab]}
        onPress={() => setActiveTab('askAI')}
      >
        <Ionicons
          name="sparkles"
          size={16}
          color={activeTab === 'askAI' ? Colors.accent.primary : Colors.text.secondary}
        />
        <Text style={[styles.tabText, activeTab === 'askAI' && styles.activeTabText]}>Ask AI</Text>
      </TouchableOpacity>
    </View>
  );

  const renderTabContent = () => {
    if (!selectedTicker) {
      return (
        <View style={styles.placeholderContainer}>
          <Ionicons name="trending-up" size={64} color={Colors.text.tertiary} />
          <Text style={styles.placeholderText}>Select a stock to view details</Text>
        </View>
      );
    }

    switch (activeTab) {
      case 'outlook':
        return (
          <IntelligentOutlookTab
            ticker={selectedTicker}
            companyName={selectedCompanyName}
            groupId={groupId}
            includeAI={includeAISentiment}
          />
        );

      case 'chart':
        // Pass groupId for future collaborative features; ChartTab ignores unknown props
        return <ChartTab ticker={selectedTicker} groupId={groupId} />;

      case 'askAI':
        return (
          <StockChatScreen
            route={{
              params: {
                ticker: selectedTicker,
                companyName: selectedCompanyName,
                stockData: stockData,
                previousMessages: conversationHistory,
              },
            }}
            navigation={navigation}
            hideHeader={true}
          />
        );

      default:
        return null;
    }
  };

  const renderRightPanel = () => (
    <View style={styles.rightPanel}>
      {renderTabs()}

      {/* AI Sentiment Toggle - Commented out for now
      {activeTab === 'outlook' && (
        <View style={styles.rightPanelToggleContainer}>
          <View style={styles.toggleRow}>
            <Switch
              value={includeAISentiment}
              onValueChange={setIncludeAISentiment}
              trackColor={{ false: Colors.background.tertiary, true: Colors.accent.primary }}
              thumbColor={Colors.background.primary}
            />
            <Text style={styles.toggleLabel}>Include AI Sentiment</Text>
          </View>
        </View>
      )}
      */}

      <View style={styles.tabContentContainer}>{renderTabContent()}</View>
    </View>
  );

  const renderLeftPanel = () => (
    <View style={styles.leftPanel}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.headerTitle}>Stocks Discussed</Text>
          <Text style={styles.headerCount}>
            {filteredArtifacts.length} of {artifacts.length} stocks
          </Text>
        </View>
        <View style={styles.headerButtons}>
          <TouchableOpacity
            onPress={handleRefreshPrices}
            disabled={refreshing}
            style={styles.refreshButton}
          >
            <Ionicons
              name="refresh"
              size={20}
              color={refreshing ? Colors.text.tertiary : Colors.accent.primary}
            />
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={Colors.text.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Stock Filter Segmented Control */}
      <View style={styles.filterContainer}>
        <View style={styles.segmentedControl}>
          <Pressable
            style={[styles.filterButton, stockFilter === 'all' && styles.filterButtonActive]}
            onPress={() => setStockFilter('all')}
          >
            <Text
              style={[
                styles.filterButtonText,
                stockFilter === 'all' && styles.filterButtonTextActive,
              ]}
            >
              All
            </Text>
          </Pressable>
          <Pressable
            style={[styles.filterButton, stockFilter === 'once' && styles.filterButtonActive]}
            onPress={() => setStockFilter('once')}
          >
            <Text
              style={[
                styles.filterButtonText,
                stockFilter === 'once' && styles.filterButtonTextActive,
              ]}
            >
              Once
            </Text>
          </Pressable>
          <Pressable
            style={[styles.filterButton, stockFilter === 'multiple' && styles.filterButtonActive]}
            onPress={() => setStockFilter('multiple')}
          >
            <Text
              style={[
                styles.filterButtonText,
                stockFilter === 'multiple' && styles.filterButtonTextActive,
              ]}
            >
              Multiple
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Stock List */}
      {filteredArtifacts.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="search-outline" size={48} color={Colors.text.tertiary} />
          <Text style={styles.emptyText}>
            {artifacts.length === 0 ? 'No stocks discussed yet' : 'No stocks match filter'}
          </Text>
          <Text style={styles.emptySubtext}>
            {artifacts.length === 0
              ? 'Mention stocks with $ like $TSLA'
              : 'Try selecting a different filter'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={filteredArtifacts}
          renderItem={renderStockItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      )}

      {/* Fetch Prices Button */}
      {artifacts.length > 0 && (
        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.fetchButton, refreshing && styles.fetchButtonDisabled]}
            onPress={handleRefreshPrices}
            disabled={refreshing}
            activeOpacity={0.7}
          >
            <Ionicons
              name="cloud-download-outline"
              size={20}
              color={refreshing ? Colors.text.tertiary : Colors.text.primary}
            />
            <Text style={[styles.fetchButtonText, refreshing && styles.fetchButtonTextDisabled]}>
              {refreshing ? 'Fetching Prices...' : 'Fetch Live Prices'}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Stocks Discussed</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={Colors.text.primary} />
          </TouchableOpacity>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.accent.primary} />
        </View>
      </View>
    );
  }

  // Large screen: Split view
  if (isLargeScreen) {
    return (
      <View style={styles.container}>
        <View style={styles.splitContainer}>
          {renderLeftPanel()}
          <View style={styles.divider} />
          {renderRightPanel()}
        </View>
      </View>
    );
  }

  // Mobile: List with modal for details
  return (
    <View style={styles.container}>
      {renderLeftPanel()}

      {/* Mobile Modal */}
      <Modal visible={showMobileModal} animationType="slide" presentationStyle="pageSheet">
        <View style={styles.modalContainer}>
          {/* Modal Header */}
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={handleCloseMobileModal} style={styles.backButton}>
              <Ionicons name="arrow-back" size={24} color={Colors.text.primary} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Stock Details</Text>
            <View style={{ width: 24 }} />
          </View>

          {/* Modal Content */}
          {renderRightPanel()}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPanel: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: Colors.border.default,
  },
  divider: {
    width: 1,
    backgroundColor: Colors.border.default,
  },
  rightPanel: {
    flex: 2,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  headerCount: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  refreshButton: {
    padding: 4,
  },
  closeButton: {
    padding: 4,
  },
  filterContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: Colors.background.tertiary,
    borderRadius: 8,
    padding: 2,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    minWidth: 70,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: Colors.accent.primary,
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: Colors.text.secondary,
  },
  filterButtonTextActive: {
    color: Colors.text.primary,
    fontWeight: '600',
  },
  rightPanelToggleContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toggleLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: Colors.text.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContainer: {
    paddingVertical: 8,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: Colors.background.primary,
  },
  selectedItem: {
    backgroundColor: Colors.background.secondary,
    borderLeftWidth: 3,
    borderLeftColor: Colors.accent.primary,
  },
  leftSection: {
    flex: 1,
    marginRight: 12,
  },
  ticker: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  companyName: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  chartSection: {
    width: 60,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rightSection: {
    alignItems: 'flex-end',
    minWidth: 70,
  },
  price: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  change: {
    fontSize: 12,
    fontWeight: '600',
  },
  messageBadge: {
    backgroundColor: Colors.background.tertiary,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 8,
    minWidth: 32,
    alignItems: 'center',
  },
  messageBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginLeft: 24,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 13,
    color: Colors.text.tertiary,
    textAlign: 'center',
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  fetchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent.primary,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 8,
  },
  fetchButtonDisabled: {
    backgroundColor: Colors.background.tertiary,
  },
  fetchButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  fetchButtonTextDisabled: {
    color: Colors.text.tertiary,
  },
  // Tab Bar
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: Colors.accent.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.text.secondary,
  },
  activeTabText: {
    color: Colors.accent.primary,
    fontWeight: '600',
  },
  tabContentContainer: {
    flex: 1,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  placeholderText: {
    fontSize: 16,
    color: Colors.text.secondary,
    marginTop: 16,
    textAlign: 'center',
  },
  // Mobile Modal
  modalContainer: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
  },
  backButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
});
